import { CONTENT_VERSION } from '@badger-sentinel/rules';
import {
  WS_CLOCK_SYNC_INTERVAL_MS,
  WS_PROTOCOL_VERSION,
  zServerMessage,
  zWsEnvelopeHeader,
  type CellViewWire,
  type CellOrdersState,
  type GmViewWire,
  type IntelReportWire,
  type MwActionPayload,
  type MwReleasedPayload,
  type MwStagePayload,
  type OrderDraft,
  type PhaseState,
  type SeatIdentity,
  type TeamOrdersState,
  type WsErrorPayload,
} from '@badger-sentinel/protocol';
import { useCallback, useEffect, useRef, useState } from 'react';

import { openSocket } from '../api/transport.js';

import { ClockEstimator } from './clock.js';

/**
 * The seat socket (§8.4, §8.5).
 *
 * Extends M0's connectivity socket rather than replacing it: the reconnect
 * backoff, the clock estimator, and the schema-checked message switch all still
 * apply. What is new is that the socket is bound to a seat, so it also carries the
 * seat's picture — and `resume`, which asks the server to replay the frames the
 * client missed while it was away rather than re-sending everything (M2-D7).
 *
 * Reconnection is not polish. A dropped connection at minute 4 of a 5-minute
 * orders window must recover on its own; the seat is held for 120 s server-side
 * precisely so that it can (§3.2).
 */

/**
 * The six states shell.md names, plus the deliberate local close.
 *
 * The split that decides the behaviour is **retryable versus terminal**.
 * `superseded` and `protocol-mismatch` stop the backoff loop dead: the token or
 * the bundle is wrong and no number of retries makes either right. Before this,
 * `closed` was set only when *this* client closed the socket, so a 1008 from the
 * server looked exactly like a network blip and the client re-attacked a dead
 * token forever.
 */
export type SeatSocketStatus =
  | 'connecting'
  | 'open'
  /** Attached, and the last attach replayed a gap rather than starting clean. */
  | 'resumed'
  | 'reconnecting'
  /** The seat's epoch is stale — GM force-release, or a claim from another device. Terminal. */
  | 'superseded'
  /** The server was redeployed underneath this tab. Terminal; needs a hard reload. */
  | 'protocol-mismatch'
  | 'closed';

/** 1008. The server refused this token; presenting it again cannot succeed. */
const WS_POLICY_VIOLATION = 1008;

/** How long `resumed` stays on the status bar before settling back to `open`. */
export const RESUMED_NOTICE_MS = 6_000;

/** A WS error frame, tagged so a dismissal sticks to the alert it was aimed at. */
export interface SeatError extends WsErrorPayload {
  readonly id: number;
  readonly atMs: number;
}

export interface SeatGmMessage {
  readonly id: number;
  readonly text: string;
  readonly severity: string;
  readonly atMs: number;
}

/** What `resume.result` reported about the gap this attach replayed (ws.ts:205). */
export interface ResumeInfo {
  readonly replayedFrom: number;
  readonly replayedCount: number;
  /** The gap was too large to replay and a fresh snapshot followed (§8.5). */
  readonly resnapshot: boolean;
}

export interface SeatSocketState {
  status: SeatSocketStatus;
  seat: SeatIdentity | null;
  view: CellViewWire | null;
  gmView: GmViewWire | null;
  phase: PhaseState | null;
  orders: CellOrdersState | null;
  team: TeamOrdersState | null;
  intel: IntelReportWire | null;
  gmMessages: SeatGmMessage[];
  /**
   * Stages that have landed this window, newest last (RB §5.3.4).
   *
   * The authoritative board is `view.mwTracks`, which arrives in the snapshot that
   * follows every stage. This is kept alongside it so the seat can show data
   * *arriving* rather than have a row appear silently — which is most of what makes
   * the mechanic feel like a warning rather than a form. Cleared on every phase
   * change, because a stage belongs to the window that produced it.
   */
  mwStages: MwStagePayload[];
  /** Releases pushed to FIRES and OW mid-window — the live unlock (decision M4-D8). */
  mwReleases: MwReleasedPayload[];
  /** Add to `Date.now()` for authoritative server time (§8.2). */
  clockOffsetMs: number;
  /** Best round trip seen, for the status bar's latency. Null until the first pong. */
  roundTripMs: number | null;
  lastSeq: number;
  errors: SeatError[];
  /** Set by the last `resume.result`; kept so the shell can say what was replayed. */
  resume: ResumeInfo | null;
  /**
   * The server's own words for a terminal close. `assertSeatCurrent` says which
   * of force-release and self-reclaim happened, and that is the only place the
   * distinction exists — so it is carried rather than paraphrased.
   */
  terminalReason: string | null;
}

const INITIAL: SeatSocketState = {
  status: 'connecting',
  seat: null,
  view: null,
  gmView: null,
  phase: null,
  orders: null,
  team: null,
  intel: null,
  gmMessages: [],
  mwStages: [],
  mwReleases: [],
  clockOffsetMs: 0,
  roundTripMs: null,
  lastSeq: -1,
  errors: [],
  resume: null,
  terminalReason: null,
};

function backoffMs(attempt: number): number {
  return Math.min(30_000, 500 * 2 ** Math.min(attempt, 6));
}

export interface SeatSocketApi extends SeatSocketState {
  sendDraft(turn: number, orders: readonly OrderDraft[]): void;
  sendSubmit(turn: number, submitted: boolean): void;
  publish(contact: string): void;
  /** IDENTIFY / RELEASE / DISMISS (RB §5.3.4). A command, not an order (M4-D8). */
  mwAction(eventId: string, action: MwActionPayload['action'], identity?: string): void;
}

export function useSeatSocket(wsUrl: string | null, seatToken: string | null): SeatSocketApi {
  const [state, setState] = useState<SeatSocketState>(INITIAL);

  const socketRef = useRef<WebSocket | null>(null);
  const clockRef = useRef(new ClockEstimator());
  const attemptsRef = useRef(0);
  const lastSeqRef = useRef(-1);
  const closedByUsRef = useRef(false);
  /**
   * Set the moment a terminal condition is detected, and read by `onclose`.
   * The detection and the close are two events — the server sends an `error`
   * frame *then* closes with 1008 — so the verdict has to outlive the frame.
   */
  const terminalRef = useRef<'superseded' | 'protocol-mismatch' | null>(null);
  const seqRef = useRef(0);

  const send = useCallback((type: string, payload: unknown) => {
    const socket = socketRef.current;
    if (socket?.readyState !== WebSocket.OPEN) return;
    socket.send(JSON.stringify({ v: WS_PROTOCOL_VERSION, seq: 0, type, payload }));
  }, []);

  const sendDraft = useCallback(
    (turn: number, orders: readonly OrderDraft[]) => {
      // The whole set, not a delta — the server stores exactly what arrives, so a
      // reconnect mid-window cannot leave the two sides disagreeing.
      send('orders.draft', { turn, orders });
    },
    [send],
  );

  const sendSubmit = useCallback(
    (turn: number, submitted: boolean) => send('orders.submit', { turn, submitted }),
    [send],
  );

  const publish = useCallback((contact: string) => send('publish.contact', { contact }), [send]);

  /**
   * The client sends no stage and no timestamp: both are the server's, because a
   * client that could name its own release stage could claim RB §5.3.6's `[+4]` at
   * any moment (§8.3).
   */
  const mwAction = useCallback(
    (eventId: string, action: MwActionPayload['action'], identity?: string) =>
      send('mw.action', { eventId, action, ...(identity !== undefined ? { identity } : {}) }),
    [send],
  );

  useEffect(() => {
    if (wsUrl === null || seatToken === null) return;

    closedByUsRef.current = false;
    terminalRef.current = null;
    let syncTimer: ReturnType<typeof setInterval> | null = null;
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let resumedTimer: ReturnType<typeof setTimeout> | null = null;
    let closeSocket = (): void => undefined;

    const connect = (): void => {
      setState((prev) => ({
        ...prev,
        status: attemptsRef.current === 0 ? 'connecting' : 'reconnecting',
      }));

      const url = `${wsUrl}?seat=${encodeURIComponent(seatToken)}`;
      const socket = openSocket(url);
      socketRef.current = socket;

      /** Hang up on a terminal verdict without pretending the user asked to leave. */
      closeSocket = (): void => socket.close(1000, 'terminal');

      socket.onopen = () => {
        attemptsRef.current = 0;
        setState((prev) => ({ ...prev, status: 'open' }));
        // `resumed` is a notice, not a resting state: it says what just
        // happened to a player who may have been mid-draft, then gets out of
        // the way. Left latched it would read as a permanent condition.
        if (resumedTimer !== null) clearTimeout(resumedTimer);
        resumedTimer = setTimeout(
          () =>
            setState((prev) => (prev.status === 'resumed' ? { ...prev, status: 'open' } : prev)),
          RESUMED_NOTICE_MS,
        );
        send('ping', { clientSentAtMs: Date.now() });
        syncTimer = setInterval(
          () => send('ping', { clientSentAtMs: Date.now() }),
          WS_CLOCK_SYNC_INTERVAL_MS,
        );
        // Ask for the gap rather than the world. The server re-snapshots on its own
        // if the gap is too large to replay, and says so.
        if (lastSeqRef.current >= 0) send('resume', { lastSeq: lastSeqRef.current });
      };

      socket.onmessage = (event: MessageEvent<string>) => {
        const clientReceivedAtMs = Date.now();
        let parsed: unknown;
        try {
          parsed = JSON.parse(event.data);
        } catch {
          return;
        }

        // The envelope's version is checked *before* the union, because the union
        // cannot express a mismatch: `v` is a `z.literal`, so a frame from a
        // newer server fails as an unparseable frame rather than as the version
        // problem it actually is. `zWsEnvelopeHeader` exists for exactly this.
        const header = zWsEnvelopeHeader.safeParse(parsed);
        if (header.success && header.data.v !== WS_PROTOCOL_VERSION) {
          terminalRef.current = 'protocol-mismatch';
          setState((prev) => ({
            ...prev,
            status: 'protocol-mismatch',
            terminalReason: `This page speaks protocol v${WS_PROTOCOL_VERSION}; the server now speaks v${header.data.v}.`,
          }));
          closeSocket();
          return;
        }

        const message = zServerMessage.safeParse(parsed);
        if (!message.success) {
          // A frame the shared schema rejects means the two ends disagree about the
          // wire format — almost always a stale tab after a deploy.
          setState((prev) => ({
            ...prev,
            errors: [
              {
                id: ++seqRef.current,
                atMs: clientReceivedAtMs,
                code: 'BAD_REQUEST' as const,
                message: 'Server sent a frame this client does not understand. Reload the page.',
              },
              ...prev.errors,
            ].slice(0, 10),
          }));
          return;
        }

        const frame = message.data;
        lastSeqRef.current = Math.max(lastSeqRef.current, frame.seq);

        switch (frame.type) {
          case 'session.hello': {
            // §10.1: a seat that was open across a redeploy is talking to a
            // server running different rule tables. `App.tsx` checks the
            // protocol number before the lobby, which does nothing for a tab
            // that has been sitting on a seat since before the deploy — this is
            // the check that covers that tab.
            if (frame.payload.contentVersion !== CONTENT_VERSION) {
              terminalRef.current = 'protocol-mismatch';
              setState((prev) => ({
                ...prev,
                status: 'protocol-mismatch',
                terminalReason: `This page carries rule content ${CONTENT_VERSION}; the server now serves ${frame.payload.contentVersion}.`,
              }));
              closeSocket();
              return;
            }
            setState((prev) => ({
              ...prev,
              seat: frame.payload.seat,
              phase: frame.payload.game ?? prev.phase,
              lastSeq: frame.seq,
            }));
            return;
          }
          case 'pong': {
            clockRef.current.observe({
              clientSentAtMs: frame.payload.clientSentAtMs,
              serverTimeMs: frame.payload.serverTimeMs,
              clientReceivedAtMs,
            });
            setState((prev) => ({
              ...prev,
              clockOffsetMs: clockRef.current.offsetMs,
              roundTripMs: clockRef.current.roundTripMs,
            }));
            return;
          }
          case 'view.snapshot':
            setState((prev) => ({
              ...prev,
              view: frame.payload.view,
              phase: frame.payload.phase,
              orders: frame.payload.orders,
              team: frame.payload.team,
              intel: frame.payload.intel ?? prev.intel,
              lastSeq: frame.seq,
            }));
            return;
          case 'gm.snapshot':
            setState((prev) => ({
              ...prev,
              gmView: frame.payload.view,
              phase: frame.payload.phase,
              lastSeq: frame.seq,
            }));
            return;
          case 'phase.changed':
            setState((prev) => ({
              ...prev,
              phase: frame.payload,
              // A stage belongs to the window that produced it. Carrying last
              // window's arrivals into this one would read as data that has landed.
              mwStages: [],
              mwReleases: [],
              lastSeq: frame.seq,
            }));
            return;
          case 'mw.stage':
            setState((prev) => ({
              ...prev,
              mwStages: [...prev.mwStages, frame.payload],
              lastSeq: frame.seq,
            }));
            return;
          case 'mw.released':
            setState((prev) => ({
              ...prev,
              mwReleases: [
                ...prev.mwReleases.filter((r) => r.eventId !== frame.payload.eventId),
                frame.payload,
              ],
              lastSeq: frame.seq,
            }));
            return;
          case 'intel.report':
            setState((prev) => ({ ...prev, intel: frame.payload, lastSeq: frame.seq }));
            return;
          case 'orders.state':
            setState((prev) => ({
              ...prev,
              orders: frame.payload.own,
              team: frame.payload.team,
              lastSeq: frame.seq,
            }));
            return;
          case 'gm.message':
            setState((prev) => ({
              ...prev,
              gmMessages: [
                {
                  id: ++seqRef.current,
                  text: frame.payload.text,
                  severity: frame.payload.severity,
                  atMs: frame.payload.atMs,
                },
                ...prev.gmMessages,
              ].slice(0, 20),
              lastSeq: frame.seq,
            }));
            return;
          case 'resume.result':
            // The gap this attach replayed. shell.md counts Resumed as its own
            // state precisely because it is not the same as a fresh connect:
            // `resnapshot` says the gap was too large and the whole picture was
            // resent, which is worth telling somebody who was mid-draft.
            setState((prev) => ({
              ...prev,
              status: 'resumed',
              resume: {
                replayedFrom: frame.payload.replayedFrom,
                replayedCount: frame.payload.replayedCount,
                resnapshot: frame.payload.resnapshot,
              },
              lastSeq: frame.seq,
            }));
            return;
          case 'error':
            setState((prev) => ({
              ...prev,
              errors: [
                { ...frame.payload, id: ++seqRef.current, atMs: clientReceivedAtMs },
                ...prev.errors,
              ].slice(0, 10),
            }));
            return;
        }
      };

      socket.onclose = (event: CloseEvent) => {
        if (syncTimer !== null) clearInterval(syncTimer);
        syncTimer = null;
        if (closedByUsRef.current) {
          setState((prev) => ({ ...prev, status: 'closed' }));
          return;
        }

        // A terminal verdict reached before the close wins: the frame that
        // carried it is gone by now, and the close code alone cannot tell a
        // redeploy from a stale token.
        const terminal = terminalRef.current;
        if (terminal !== null) {
          setState((prev) => ({ ...prev, status: terminal }));
          return;
        }

        // 1008 from this server means one thing: the seat token was refused
        // (`ws/session.ts` — a bad token, or an epoch the seat row has moved
        // past). Retrying presents the same dead token, which is what the old
        // handler did, forever. Superseded is terminal and the route back is
        // entry, so the loop stops here.
        if (event.code === WS_POLICY_VIOLATION) {
          terminalRef.current = 'superseded';
          setState((prev) => ({
            ...prev,
            status: 'superseded',
            terminalReason:
              prev.errors[0]?.message ??
              'This seat has been claimed by another device or released by the GM.',
          }));
          return;
        }

        const delay = backoffMs(attemptsRef.current);
        attemptsRef.current += 1;
        setState((prev) => ({ ...prev, status: 'reconnecting' }));
        reconnectTimer = setTimeout(connect, delay);
      };
    };

    connect();

    return () => {
      closedByUsRef.current = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      if (resumedTimer !== null) clearTimeout(resumedTimer);
      if (syncTimer !== null) clearInterval(syncTimer);
      socketRef.current?.close(1000, 'component unmounted');
      socketRef.current = null;
      clockRef.current.reset();
    };
  }, [wsUrl, seatToken, send]);

  return { ...state, sendDraft, sendSubmit, publish, mwAction };
}
